// src/Utils/schema.js
// Yhtenäiset apurit varauskalenterin skeemaan (labelit, päivät, hinnat, normalisointi)

import { getDay } from 'date-fns';

/**
 * Viikonpäivien pitkä suomi-muoto samalla tavalla kuin varattavatajat-taulukossa.
 * getDay(): 0=Su..6=La
 */
export const LONG_DAYS_FI = [
  'Sunnuntai',
  'Maanantai',
  'Tiistai',
  'Keskiviikko',
  'Torstai',
  'Perjantai',
  'Lauantai',
];

/**
 * Palauttaa viikonpäivän pitkän nimen annetusta Date-oliosta.
 */
export function dayLongFromDate(date) {
  return LONG_DAYS_FI[getDay(date)];
}

/**
 * Muodosta aikavälin label tunnista h: (6 → "06:00 - 07:00")
 */
export function labelForHourPadded(hour) {
  const h = Number(hour) || 0;
  const s = String(h).padStart(2, '0');
  const e = String(h + 1).padStart(2, '0');
  return `${s}:00 - ${e}:00`;
}

/**
 * Kompakti label vertailuun (6 → "6:00-7:00")
 * Hyödyllinen, koska tietokannassa voi olla eri tavoin kirjoitettuja labelleita.
 */
export function labelForHourCompact(hour) {
  const h = Number(hour) || 0;
  return `${h}:00-${h + 1}:00`;
}

/**
 * Normalisoi mikä tahansa "Aika" -label vertailua varten:
 * - poistaa välit
 * - poistaa johtavan nollan tunnista
 * Esim. "06:00 - 07:00" → "6:00-7:00"
 */
export function normalizeTimeLabel(raw) {
  if (!raw) return '';
  return String(raw)
    .replace(/\s+/g, '')
    .replace(/\b0(\d):/g, '$1:'); // 06: -> 6:
}

/**
 * Turvallinen numeroksi muunto. Hyväksyy string/number ja palauttaa oletuksen jos ei kelpaa.
 */
export function toNumberSafe(val, fallback = 0) {
  if (val == null || val === '') return fallback;
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Normalisoi yhden varattavatajat-rivin yhteen, vakioon muotoon.
 * @param {object} row - esim:
 *  {
 *    Aika: "06:00 - 07:00",
 *    Maanantai: { open:true, price: 5 } // tai { open:true, hinta:5 }
 *    Tiistai: ...
 *  }
 * @returns {object} {
 *   timeLabelPadded: "06:00 - 07:00",
 *   timeLabelCompact: "6:00-7:00",
 *   byDay: {
 *     Maanantai: { open:boolean, price:number },
 *     ...
 *   }
 * }
 */
export function normalizeVarattavatajatRow(row = {}) {
  const timeLabelRaw = row.Aika || row.aika || '';
  const timeLabelPadded = coerceToPaddedLabel(timeLabelRaw);
  const timeLabelCompact = normalizeTimeLabel(timeLabelRaw);

  const byDay = {};
  for (const day of LONG_DAYS_FI) {
    const cell = row[day] || {};
    // Hyväksy sekä price että hinta. Stringit → number.
    const price = cell.hinta ?? cell.price ?? 0;
    byDay[day] = {
      open: !!cell.open,
      price: toNumberSafe(price, 0),
    };
  }

  return { timeLabelPadded, timeLabelCompact, byDay };
}

/**
 * Palauta yhdestä rivistä (normalisoimatta tai normalisoituna) tietyn päivän celli.
 * @param {object} row - alkuperäinen rividata (kuten kannassa)
 * @param {Date|string} dayInput - Date tai päivästringi "Maanantai"…"Sunnuntai"
 * @returns {{open:boolean, price:number}}
 */
export function getCellFromRow(row = {}, dayInput) {
  const dayName =
    typeof dayInput === 'string' ? dayInput : dayLongFromDate(dayInput);
  const cell = row[dayName] || {};
  const price = cell.hinta ?? cell.price ?? 0;
  return { open: !!cell.open, price: toNumberSafe(price, 0) };
}

/**
 * Hae rivitaulukosta rivi tietylle tunnille. Palauttaa normalisoidun rivin tai null.
 * @param {Array<object>} rows - varattavatajat-taulukko
 * @param {number} hour - alkutunti
 */
export function findRowForHour(rows = [], hour) {
  const want = normalizeTimeLabel(labelForHourPadded(hour));
  for (const r of rows) {
    const lbl = normalizeTimeLabel(r?.Aika || r?.aika);
    if (lbl && lbl === want) {
      return normalizeVarattavatajatRow(r);
    }
  }
  return null;
}

/**
 * Apufunktio: jos label on tyhjä, luodaan varmuuden vuoksi padded-label tunnista,
 * muuten palautetaan sellaisenaan. Kätevä kun kannassa on sekalaisia muotoja.
 */
function coerceToPaddedLabel(raw) {
  const s = String(raw || '');
  if (!s) return labelForHourPadded(0); // fallback
  // Jos näyttää tyyliä "6:00 - 7:00" → padataan:
  const m = s.match(/^(\d{1,2}):00\s*-\s*(\d{1,2}):00$/);
  if (m) {
    const h = Number(m[1]) || 0;
    return labelForHourPadded(h);
  }
  return s;
}

/**
 * Pieni utili: muodosta ReservationsContextin käyttämä dayKey "eee dd.MM.yyyy" ilman locale-riippuvuutta täällä.
 * Suosittelen jatkossakin käyttämään date-fns:ää siihen, mutta jos tarvitset vakaata avainta ilman localea,
 * tämä palauttaa muodon "YYYY-MM-DD".
 */
export function stableDayKey(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
